import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AgeGate } from "@/components/AgeGate";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Verify from "@/pages/Verify";
import COAHub from "@/pages/COAHub";
import Library from "@/pages/Library";
import Article from "@/pages/Article";
import Quality from "@/pages/Quality";
import Calculator from "@/pages/Calculator";
import Compliance from "@/pages/Compliance";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import ShippingPolicy from "@/pages/ShippingPolicy";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Checkout from "@/pages/Checkout";
import Payment from "@/pages/Payment";
import OrderConfirm from "@/pages/OrderConfirm";
import MyOrders from "@/pages/MyOrders";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminCOAs from "@/pages/admin/COAs";
import AdminOrders from "@/pages/admin/Orders";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminArticles from "@/pages/admin/Articles";
import AdminScanner from "@/pages/admin/Scanner";
import AdminSuppliers from "@/pages/admin/Suppliers";
import AdminInventory from "@/pages/admin/Inventory";
import AdminBookkeeping from "@/pages/admin/Bookkeeping";
import AdminCOAReview from "@/pages/admin/COAReview";
import AdminAffiliates from "@/pages/admin/Affiliates";
import AdminPromoCodes from "@/pages/admin/PromoCodes";
import AdminBackup from "@/pages/admin/Backup";
import AdminShipping from "@/pages/admin/Shipping";
import AdminLoyalty from "@/pages/admin/Loyalty";
import AdminPayments from "@/pages/admin/Payments";

import AccountLayout from "@/pages/account/AccountLayout";
import AccountProfile from "@/pages/account/Profile";
import AccountAddresses from "@/pages/account/Addresses";
import AccountInvoices from "@/pages/account/Invoices";
import AccountCOAs from "@/pages/account/COAs";
import AccountAffiliate from "@/pages/account/Affiliate";
import AccountPoints from "@/pages/account/Points";
import AccountWatchlist from "@/pages/account/Watchlist";

import "@/App.css";

export default function App() {
  useEffect(() => {
    document.title = "Nebula Aminos · Reference-Grade Research Compounds for Labs";
    // Affiliate referral capture: ?ref=CODE → persist + count the click
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      const code = ref.toUpperCase();
      localStorage.setItem("nebula_ref_code", code);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/track-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }).catch(() => {});
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AgeGate />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/shop" element={<Layout><Shop /></Layout>} />
            <Route path="/shop/:sku" element={<Layout><ProductDetail /></Layout>} />
            <Route path="/coa" element={<Layout><COAHub /></Layout>} />
            <Route path="/verify" element={<Layout><Verify /></Layout>} />
            <Route path="/library" element={<Layout><Library /></Layout>} />
            <Route path="/library/:slug" element={<Layout><Article /></Layout>} />
            <Route path="/quality" element={<Layout><Quality /></Layout>} />
            <Route path="/calculator" element={<Layout><Calculator /></Layout>} />
            <Route path="/compliance" element={<Layout><Compliance /></Layout>} />
            <Route path="/terms" element={<Layout><Terms /></Layout>} />
            <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
            <Route path="/refund-policy" element={<Layout><RefundPolicy /></Layout>} />
            <Route path="/shipping-policy" element={<Layout><ShippingPolicy /></Layout>} />
            <Route path="/login" element={<Layout><Login /></Layout>} />
            <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
            <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
            <Route path="/auth/callback" element={<Layout><AuthCallback /></Layout>} />
            <Route path="/register" element={<Layout><Register /></Layout>} />
            <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
            <Route path="/pay/:orderNumber" element={<Layout><ProtectedRoute><Payment /></ProtectedRoute></Layout>} />
            <Route path="/order/:orderNumber" element={<Layout><ProtectedRoute><OrderConfirm /></ProtectedRoute></Layout>} />

            <Route path="/account" element={<Layout><ProtectedRoute><AccountLayout /></ProtectedRoute></Layout>}>
              <Route index element={<AccountProfile />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="addresses" element={<AccountAddresses />} />
              <Route path="invoices" element={<AccountInvoices />} />
              <Route path="coas" element={<AccountCOAs />} />
              <Route path="affiliate" element={<AccountAffiliate />} />
              <Route path="points" element={<AccountPoints />} />
              <Route path="watchlist" element={<AccountWatchlist />} />
            </Route>

            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="bookkeeping" element={<AdminBookkeeping />} />
              <Route path="coas" element={<AdminCOAs />} />
              <Route path="coa-review" element={<AdminCOAReview />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="affiliates" element={<AdminAffiliates />} />
              <Route path="promo-codes" element={<AdminPromoCodes />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="scanner" element={<AdminScanner />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="loyalty" element={<AdminLoyalty />} />
              <Route path="payments" element={<AdminPayments />} />
            </Route>

            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
